import { defineStore } from 'pinia'
import backendHandler from '@/BackendAPI'
import { useServiceProviderStore } from '@/store/serviceProviders'
import { useSettingsStore } from '@/store/settings'
import { buildDiscoveryObject } from '@/composables/discovery.js'
import _ from 'lodash'
import OwnPermissionsSyncable from '@/syncing/syncables/OwnPermissionsSyncable.js'
import { useLogsStore } from '@/store/logs'
import { Buffer } from 'buffer'
import { getOrigin, isInDataCollectorMode } from '@/composables/version.js'
import VersionSyncable from '@/syncing/syncables/VersionSyncable.js'
import SyncableHandler from '@/syncing/SyncableHandler.js'
import { useEphemeralBackendInstancesStore } from '@/store/ephemeralBackendInstances.js'

export const useInstanceStore = defineStore('backendInstances', {
  state: () => ({
    instances: {},
  }),
  persist: {
    pick: ['instances'],
  },

  share: {
    enable: true,
    initialize: true,
  },

  getters: {
    currentInstanceType: state => {
      if (!state.selectedInstanceId) return null
      return state.instances[state.selectedInstanceId]?.instanceType || null
    },
    isPerformingRequestOnSelectedInstance: state => {
      if (!state.selectedInstanceId) return false
      return backendHandler.getOrCreateBackendAPIInstance(state.selectedInstanceId).accessibleState.concurrentRequests > 0
    },

    attemptingAutoLoginOnInstance: () => globalUniqueIdentifier => useEphemeralBackendInstancesStore().attemptingAutoLogin[globalUniqueIdentifier],

    isOnline: () => globalUniqueIdentifier => backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).accessibleState.isOnline,

    isLoggedIn: () => globalUniqueIdentifier => backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).accessibleState.loggedIn,

    getCurrentAccountId: state => {
      if (!state.selectedInstanceId) return null
      return backendHandler.getOrCreateBackendAPIInstance(state.selectedInstanceId).accessibleState.accountId
    },

    getSenator: state => state.instances['66'],

    getAdmiral: state => Object.values(state.instances).findLast(instance => instance.instanceType === 'admiral'),

    globalUniqueIdentifiers: state => Object.keys(state.instances),

    globalUniqueIdentifiersForBarns: state =>
      Object.keys(state.instances).filter(globalUniqueIdentifier => state.instances[globalUniqueIdentifier].instanceType === 'barn'),

    getAccountIdForInstance: () => globalUniqueIdentifier =>
      backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).accessibleState.accountId,

    connectionStrategy: () => globalUniqueIdentifier =>
      backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).accessibleState.connectionStrategy,

    selectedInstanceId() {
      return this.router.currentRoute.value.params?.globalUniqueIdentifier
    },

    selectedInstance(state) {
      return state.instances[this.selectedInstanceId]
    },

    getSelectedInstanceSpecies: state => state.instances[state.selectedInstanceId]?.species,

    getSpecies: state => globalUniqueIdentifier => state.instances[globalUniqueIdentifier].species,

    hasInstanceWithActiveRemoteAuthentication: state => {
      return Object.values(state.instances).some(instance => {
        const refreshToken = instance.refreshToken
        try {
          const decodedToken = JSON.parse(Buffer.from(refreshToken.split('.')[1], 'base64').toString())
          return decodedToken.authenticationProvider === 'remote'
        } catch {
          return false
        }
      })
    },

    getAllFolderNames: state => {
      let instances = Object.values(state.instances)
      if (isInDataCollectorMode()) {
        instances = instances.filter(instance => instance.instanceType === 'data_collector')
      }
      return [...new Set(instances.map(instance => instance.folderName).filter(folderName => !!folderName))].toSorted()
    },

    getAllOnlineInstances(state) {
      return Object.values(Object.values(state.instances)).filter(instance => this.isOnline(instance.globalUniqueIdentifier))
    },
  },

  actions: {
    updateInstance(globalUniqueIdentifier, propertyPath, value) {
      if (!this.instances[globalUniqueIdentifier]) throw new Error('Instance not found: ' + globalUniqueIdentifier)
      const properties = propertyPath.split('.')
      let currentObject = this.instances[globalUniqueIdentifier]
      for (let i = 0; i < properties.length - 1; i++) {
        currentObject = currentObject[properties[i]]
      }
      if (
        value !== currentObject[properties[properties.length - 1]] && //this is a cheap operation to check if the reference is the same
        !_.isEqual(value, currentObject[properties[properties.length - 1]]) //this is a deep check to see if the value is actually different
      ) {
        currentObject[properties[properties.length - 1]] = value
      }
    },

    async loadSpecies(globalUniqueIdentifier) {
      const labelResponse = await backendHandler.get(globalUniqueIdentifier, { path: '/api/data/labels', automaticTriggeredRequest: true })
      if (!labelResponse.ok) return
      this.updateInstance(globalUniqueIdentifier, 'labels', labelResponse.data)
      if (labelResponse.data.length === 0) return
      if (labelResponse.data.includes('pig_lying') || labelResponse.data.includes('pig'))
        this.updateInstance(globalUniqueIdentifier, 'species', 'pigs')
      else if (labelResponse.data.includes('Broiler')) this.updateInstance(globalUniqueIdentifier, 'species', 'chickens')
    },

    addAllInstancesToBackendHandler() {
      for (const globalUniqueIdentifier of Object.keys(this.instances)) {
        backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier)
        useInstanceStore().keepOwnPermissionsLoaded(globalUniqueIdentifier)
        useInstanceStore().keepVersionLoaded(globalUniqueIdentifier)
      }
    },

    addNewBackendInstance(globalUniqueIdentifier, name, discoveryObject, type) {
      const instance = {
        globalUniqueIdentifier,
        name,
        discoveryObject,
        instanceType: type,
        online: false,
        version: {},
        refreshToken: null,
        autoLoginWithRemoteAccount: true,
      }
      this.instances[globalUniqueIdentifier] = instance
      return instance
    },

    removeBackendInstance(globalUniqueIdentifier) {
      backendHandler.removeInstance(globalUniqueIdentifier)
      delete this.instances[globalUniqueIdentifier]
      delete useEphemeralBackendInstancesStore().availableAuthenticationMethods[globalUniqueIdentifier]
    },

    updateInstanceName(globalUniqueIdentifier, name) {
      if (!this.instances[globalUniqueIdentifier]) return
      this.updateInstance(globalUniqueIdentifier, 'name', name)
    },

    async fetchVersion(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const response = await backendHandler.get(globalUniqueIdentifier, { path: '/api/v', requiresAuth: false, automaticTriggeredRequest: true })
      if (!response.ok) return
      return response.data
    },

    async loadVersion(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const version = await this.fetchVersion(globalUniqueIdentifier)
      if (!version) return
      if (version && _.isEqual(this.instances[globalUniqueIdentifier].version, version)) return
      this.updateInstance(globalUniqueIdentifier, 'version', version)
    },

    async loadAvailableAuthenticationMethods(globalUniqueIdentifier) {
      const usernamePasswordResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/auth/usernamePassword',
        requiresAuth: false,
        priority: 1,
      })
      const emailPasswordResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/auth/emailPassword',
        requiresAuth: false,
        priority: 1,
      })
      const remoteResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/auth/remote',
        requiresAuth: false,
        priority: 1,
      })

      useEphemeralBackendInstancesStore().availableAuthenticationMethods[globalUniqueIdentifier] = {
        usernamePassword: usernamePasswordResponse.ok,
        emailPassword: emailPasswordResponse.ok,
        remote: remoteResponse.ok,
      }
    },

    async autoLoginIntoInstanceIfPossible(globalUniqueIdentifier) {
      if (useSettingsStore().enableAutoLogin === false) return
      if (this.instances[globalUniqueIdentifier].autoLoginWithRemoteAccount === false) return
      if (useEphemeralBackendInstancesStore().alreadyTriedAutoLogin[globalUniqueIdentifier]) return
      useLogsStore().addLogEntry({
        message: 'Attempting Auto Login',
        tag: 'backendInstances',
        globalUniqueIdentifier: globalUniqueIdentifier,
        level: 'DEBUG',
      })
      useEphemeralBackendInstancesStore().alreadyTriedAutoLogin[globalUniqueIdentifier] = true

      useEphemeralBackendInstancesStore().attemptingAutoLogin[globalUniqueIdentifier] = true
      if (!Object.keys(useEphemeralBackendInstancesStore().availableAuthenticationMethods).includes(globalUniqueIdentifier)) {
        await this.loadAvailableAuthenticationMethods(globalUniqueIdentifier)
      }
      if (!useEphemeralBackendInstancesStore().availableAuthenticationMethods[globalUniqueIdentifier].remote) {
        useEphemeralBackendInstancesStore().attemptingAutoLogin[globalUniqueIdentifier] = false
        useLogsStore().addLogEntry({
          message: 'Auto Login not possible, no remote authentication available',
          tag: 'backendInstances',
          globalUniqueIdentifier: globalUniqueIdentifier,
          level: 'DEBUG',
        })
        return
      }
      const { accountExists, serviceProviderId } = await useServiceProviderStore().checkIfRemoteAccountExists(globalUniqueIdentifier)
      if (!accountExists) {
        useEphemeralBackendInstancesStore().attemptingAutoLogin[globalUniqueIdentifier] = false
        useLogsStore().addLogEntry({
          message: 'Auto Login not possible, no remote account exists',
          tag: 'backendInstances',
          globalUniqueIdentifier: globalUniqueIdentifier,
          level: 'DEBUG',
          httpMethod: 'POST',
        })
        return
      }
      await this.loginWithRemoteAuthentication(globalUniqueIdentifier, serviceProviderId)

      useEphemeralBackendInstancesStore().attemptingAutoLogin[globalUniqueIdentifier] = false
    },

    async logOutOfInstance(globalUniqueIdentifier) {
      this.updateInstance(globalUniqueIdentifier, 'autoLoginWithRemoteAccount', false)
      await backendHandler.logOutOfInstance(globalUniqueIdentifier)
    },

    async getAuthorizationRecords(accountId, accountType = 'account', valid = undefined, projection = undefined) {
      const query = [
        { key: 'targetEntity.entityId', operator: '==', value: accountId },
        { key: 'targetEntity.entityType', operator: '==', value: accountType },
      ]

      if (valid !== undefined) {
        query.push({ key: 'valid', operator: '==', value: valid })
      }

      const authorizationRecords = await backendHandler.post(this.selectedInstanceId, {
        path: '/api/authorization/query',
        data: {
          query: query,
          projection: projection,
        },
      })
      if (!authorizationRecords.ok) return []
      return authorizationRecords.data
    },

    async addAuthorizationRecord(accountId, roles, permissions, accountType = 'account', validFrom = Date.now(), validUntil = -1) {
      const authRecordData = {
        authorizationRecord: {
          targetEntity: {
            entityType: accountType,
            entityId: accountId,
          },
          directRoles: [...roles],
          directPermissions: [...permissions],
          validFrom: validFrom,
          validUntil: validUntil,
        },
      }

      const authorizationRecordResponse = await backendHandler.post(this.selectedInstanceId, {
        path: '/api/authorization',
        data: authRecordData,
      })
      if (!authorizationRecordResponse.ok) return null
      return authorizationRecordResponse.data
    },

    async invalidateAuthorizationRecord(authorizationRecordId) {
      const authorizationRecord = await backendHandler.delete(this.selectedInstanceId, {
        path: `/api/authorization/${authorizationRecordId}`,
      })
      return authorizationRecord.ok
    },

    async invalidateAllAuthorizationRecords(accountId, accountType = 'account') {
      const authorizationRecord = await backendHandler.delete(this.selectedInstanceId, {
        path: `/api/authorization/${accountType}/${accountId}`,
      })
      return authorizationRecord.ok
    },

    async removeRolesAndPermissionsFromAccount(accountId, roles, permissions, accountType = 'account') {
      const authorizationRecords = await backendHandler.patch(this.selectedInstanceId, {
        path: `/api/authorization/${accountType}/${accountId}`,
        data: {
          roles: roles,
          permissions: permissions,
        },
      })
      return authorizationRecords.ok
    },

    async getUptime(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const response = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/uptime',
      })
      if (!response.ok) return null
      return response.data
    },

    async getPredefinedRoles(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const roleResponse = await backendHandler.get(globalUniqueIdentifier, { path: '/api/authorization-roles/predefined' })
      if (!roleResponse.ok) return []
      return roleResponse.data
    },

    async getCustomRoles(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const roleResponse = await backendHandler.post(globalUniqueIdentifier, { path: '/api/authorization-roles/query' })
      if (!roleResponse.ok) return []
      return roleResponse.data
    },

    async getAllRoles(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      return [...(await this.getPredefinedRoles(globalUniqueIdentifier)), ...(await this.getCustomRoles(globalUniqueIdentifier))]
    },

    /**
     * This function retrieves all roles of an account recursively, including inherited roles.
     *
     * @param {string} accountId - The ID of the account to retrieve roles for.
     * @param {string} accountType - The type of the account to retrieve roles for.
     * @param {Role[]} allRoles - A list of all roles.
     *
     * @returns {Promise<Role[]>} A list of roles of the account.
     */
    async getRolesOfAccount(accountId, accountType = 'account', allRoles = undefined) {
      const authRecords = await this.getAuthorizationRecords(accountId, accountType, true)
      const directRolesRaw = [...new Set(authRecords.map(record => record.directRoles).flat())]

      if (!allRoles) {
        const allRoles = await this.getAllRoles()
        const directRolesRecursive = this.getRoleAndAllInheritedRolesAsObjectRecursive(directRolesRaw, allRoles)
        return directRolesRecursive
      }

      const directRolesRecursive = this.getRoleAndAllInheritedRolesAsObjectRecursive(directRolesRaw, allRoles)
      return directRolesRecursive
    },

    async getAllPermissions(skipPermissionSimulation = false) {
      const permissionResponse = await backendHandler.get(this.selectedInstanceId, {
        path: '/api/authorization/all_permissions',
        skipPermissionSimulation,
      })
      if (!permissionResponse.ok) return []
      return Object.values(permissionResponse.data)
    },

    async getSpecificRole(roleId) {
      const roleResponse = await backendHandler.get(this.selectedInstanceId, {
        path: '/api/authorization-roles/' + roleId,
      })
      if (!roleResponse.ok) return null
      return roleResponse.data
    },
    /**
     * This function retrieves all permissions of an account.
     *
     * @param {string} accountId - The ID of the account to retrieve permissions for.
     * @param {string} accountType - The type of the account to retrieve permissions for.
     * @param {Permission[]} allPermissions - A list of all permissions.
     *
     * @param skipPermissionSimulation
     * @returns {Promise<Permission[]>} A list of permissions of the account.
     */
    async getPermissionsOfAccount(accountId, accountType = 'account', allPermissions = undefined, skipPermissionSimulation = false) {
      const permissionResponse = await backendHandler.get(this.selectedInstanceId, {
        path: `/api/authorization/${accountType}/${accountId}/permissions`,
        skipPermissionSimulation,
      })
      if (!permissionResponse.ok) return []

      if (!allPermissions) {
        const allPermissions = await this.getAllPermissions(skipPermissionSimulation)
        const accountPermissionsObject = allPermissions.filter(permission => permissionResponse.data.includes(permission.id))
        return accountPermissionsObject
      }

      const accountPermissionsObject = allPermissions.filter(permission => permissionResponse.data.includes(permission.id))
      return accountPermissionsObject
    },

    /**
     * This function retrieves all permissions, including inherited permissions, of the role.
     *
     * @param {string} roleId - The ID of the role to retrieve permissions for.
     *
     * @returns {Promise<Permission[]>} A list of all permissions of the role.
     */
    async getAllInheritedPermissionsOfRole(roleId) {
      const roleResponse = await backendHandler.get(this.selectedInstanceId, { path: `/api/authorization-roles/${roleId}/permissions` })
      if (!roleResponse.ok) return []
      return roleResponse.data
    },

    /**
     * This function retrieves all roles that the account has permissions for.
     *
     * @param {string} accountId - The ID of the account to retrieve roles for.
     * @param {string} accountType - The type of the account to retrieve roles for.
     * @param {Role[]} allRoles - A list of all roles.
     * @param {Permission[]} allPermissions - A list of all permissions.
     *
     * @returns {Promise<Role[]>} A list of roles that the account has all permissions for.
     */
    async getRolesTheAccountHasAllPermissionsFor(accountId, accountType = 'account', allRoles = undefined, allPermissions = undefined) {
      if (!allRoles) {
        allRoles = await this.getAllRoles()
      }
      const permissionsOfAccount = await this.getPermissionsOfAccount(accountId, accountType, allPermissions)
      const allowedRoles = []

      for (const role of allRoles) {
        const permissionsOfRole = await this.getAllInheritedPermissionsOfRole(role.authorizationRoleId)
        const isAllowed = permissionsOfRole.every(rolePermission =>
          permissionsOfAccount.some(accountPermission => accountPermission.id === rolePermission)
        )
        if (isAllowed) allowedRoles.push(role)
      }

      return allowedRoles
    },

    /**
     * This function retrieves all roles and permissions of an account recursively, including inherited roles and permissions.
     *
     * @param {string} ownerId - The ID of the account to retrieve roles and permissions for.
     * @param {string} accountType - The type of the account to retrieve roles and permissions for.
     *
     * @returns {Promise<Object>} An object containing the owner's roles and permissions.
     */
    async getServiceAccountOwnersRolesAndPermissions(ownerId, accountType = 'account', allRoles = undefined, allPermissions = undefined) {
      // Get all roles and inherited roles of the owner
      if (!allRoles) {
        allRoles = await this.getAllRoles()
      }
      const directRolesRecursive = await this.getRolesOfAccount(ownerId, accountType, allRoles)

      // Get all permissions of the owner
      if (!allPermissions) {
        allPermissions = await this.getAllPermissions()
      }
      const permissionsOfAccount = await this.getPermissionsOfAccount(ownerId, accountType, allPermissions)

      // The owner may have direct permissions assigned outside of any role.
      // To determine if we can assign a role to the owner, we need to check if the owner has all permissions of the role.
      // If all role's permission are included in the entirety of the owner's permissions, we can assign the role to the owner.
      const rolesTheAccountHasAllPermissionFor = await this.getRolesTheAccountHasAllPermissionsFor(ownerId, accountType, allRoles, allPermissions)

      return {
        ownerRoles: [...new Set([...directRolesRecursive, ...rolesTheAccountHasAllPermissionFor])],
        ownerPermissions: permissionsOfAccount,
      }
    },

    /**
     * Recursively retrieves all inherited role-objects for a given list of role IDs
     *
     * @param {string[]} roles - A list of role IDs.
     * @param {Role[]} allRoles - A list of all roles.
     * @returns {Role[]} A list of unique role objects.
     */
    getRoleAndAllInheritedRolesAsObjectRecursive(roles, allRoles) {
      const allRolesOfAccount = []

      roles.forEach(async roleId => {
        // find the role object by its ID and add it to the list of all roles
        const roleObject = allRoles.find(role => role.authorizationRoleId === roleId)
        if (!roleObject) return
        allRolesOfAccount.push(roleObject)

        // recursively add inherited roles of the current role
        if (roleObject.inheritedRoles.length > 0) {
          allRolesOfAccount.push(...this.getRoleAndAllInheritedRolesAsObjectRecursive(roleObject.inheritedRoles, allRoles))
        }
      })

      return [...new Set(allRolesOfAccount)]
    },

    async deleteAccount(accountId) {
      if (!this.hasPermission('accounts.manage.advanced')) return null
      const accountResponse = await backendHandler.delete(this.selectedInstanceId, { path: '/api/accounts/' + accountId })
      if (!accountResponse.ok) return null
      return accountResponse.data
    },

    async fetchOwnPermissions(globalUniqueIdentifier) {
      try {
        const permissionResponse = await backendHandler.get(globalUniqueIdentifier, {
          path: '/api/permissions',
          skipPermissionSimulation: true,
        })
        if (permissionResponse.ok) {
          const currentAvailablePermissions = this.instances[globalUniqueIdentifier].availablePermissions
          const newPermissions = permissionResponse.data
          if (currentAvailablePermissions && _.isEqual(currentAvailablePermissions, newPermissions)) return
          this.updateInstance(globalUniqueIdentifier, 'availablePermissions', newPermissions)
        }
      } catch (e) {
        useLogsStore().addLogEntry({
          message: 'Error while fetching own permissions',
          error: e,
          tag: 'backendInstances',
          level: 'ERROR',
          globalUniqueIdentifier: globalUniqueIdentifier,
          httpMethod: 'GET',
        })
      }
    },

    async loginWithRemoteAuthentication(globalUniqueIdentifier, serviceProviderId) {
      try {
        const signInResponse = await backendHandler.get('66', { path: '/api/idp/' + serviceProviderId + '/login' })
        if (!signInResponse.ok) return false
        const { refreshToken } = signInResponse.data
        this.updateInstance(globalUniqueIdentifier, 'refreshToken', refreshToken)
        backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).validateRefreshToken()
        await this.ensureOwnPermissionsLoaded(globalUniqueIdentifier)
        //        await this.fetchOwnPermissions(globalUniqueIdentifier);
        useLogsStore().addLogEntry({
          message: 'Auto Login Successful! (Service Provider) ' + serviceProviderId,
          tag: 'backendInstances',
          globalUniqueIdentifier: globalUniqueIdentifier,
          level: 'INFO',
          httpMethod: 'GET',
        })
        return true
      } catch (e) {
        useLogsStore().addLogEntry({
          message: 'Auto Login failed! (Service Provider) ' + serviceProviderId,
          tag: 'backendInstances',
          error: e,
          globalUniqueIdentifier: globalUniqueIdentifier,
          level: 'ERROR',
          httpMethod: 'GET',
        })
        return false
      }
    },

    async registerViaServiceProvider(globalUniqueIdentifier, serviceProviderId, displayName, locale, timezone, acceptEula) {
      try {
        const registerResponse = await backendHandler.post('66', {
          path: '/api/idp/' + serviceProviderId + '/account',
          data: { displayName, acceptEula, locale, timezone },
        })
        return registerResponse.ok
      } catch {
        return false
      }
    },

    async registerWithEmailAndPassword(globalUniqueIdentifier, email, password, passwordRepetition, displayName, locale, acceptEula) {
      const registerResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/emailPassword',
        data: {
          email,
          password,
          passwordRepetition,
          displayName,
          locale,
          acceptEula,
          frontendUrl: getOrigin() + '/ql/email-verification',
        },
        requiresAuth: false,
      })
      return registerResponse.ok
    },

    async requestNewConfirmationEmail(globalUniqueIdentifier, email) {
      const confirmationResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/emailPassword/verification/new',
        data: {
          email,
          frontendUrl: getOrigin() + '/ql/email-verification',
        },
        requiresAuth: false,
      })
      return confirmationResponse.ok
    },

    async requestNewPasswordLink({ email, discoveryString, globalUniqueIdentifier }) {
      await this.addInstanceFromDiscoveryString(discoveryString, globalUniqueIdentifier)
      if (globalUniqueIdentifier === false) return false
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/emailPassword/verification/new',
        data: {
          email: email,
          frontendUrl: getOrigin() + '/ql/account-provisioning',
        },
        requiresAuth: false,
      })
      return response.ok
    },

    async loginWithEmailAndPassword(globalUniqueIdentifier, email, password) {
      try {
        const signInResponse = await backendHandler.post(globalUniqueIdentifier, {
          path: '/api/auth/emailPassword/login',
          data: { email, password },
          requiresAuth: false,
        })
        if (!signInResponse.ok) return { successful: false, emailConfirmationNeeded: signInResponse.status === 412 }

        const { refreshToken } = signInResponse.data
        this.updateInstance(globalUniqueIdentifier, 'refreshToken', refreshToken)
        backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).validateRefreshToken()
        await this.ensureOwnPermissionsLoaded(globalUniqueIdentifier)
        this.updateInstance(globalUniqueIdentifier, 'email', email)
        return { successful: true }
      } catch {
        return { successful: false, emailConfirmationNeeded: false }
      }
    },

    async registerWithUsernameAndPassword(globalUniqueIdentifier, username, password, passwordRepetition, displayName, locale, acceptEula) {
      const registerResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/usernamePassword',
        data: { username, password, passwordRepetition, displayName, locale, acceptEula },
        requiresAuth: false,
      })
      return registerResponse.ok
    },

    async loginWithUsernameAndPassword(globalUniqueIdentifier, username, password) {
      try {
        const signInResponse = await backendHandler.post(globalUniqueIdentifier, {
          path: '/api/auth/usernamePassword/login',
          data: { username, password },
          requiresAuth: false,
        })
        if (!signInResponse.ok) return false

        const { refreshToken } = signInResponse.data
        this.updateInstance(globalUniqueIdentifier, 'refreshToken', refreshToken)
        backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).validateRefreshToken()
        await this.ensureOwnPermissionsLoaded(globalUniqueIdentifier)
        return true
      } catch {
        return false
      }
    },

    hasAllPermissions(permissions, overrideGlobalUniqueIdentifier = undefined, skipSimulation = false) {
      for (const permission of permissions) {
        if (!this.hasPermission(permission, overrideGlobalUniqueIdentifier, skipSimulation)) return false
      }
      return true
    },

    hasPermission(permission, overrideGlobalUniqueIdentifier, skipSimulation = false) {
      const globalUniqueIdentifier = overrideGlobalUniqueIdentifier !== undefined ? overrideGlobalUniqueIdentifier : this.selectedInstanceId
      if (!globalUniqueIdentifier || !Object.keys(this.instances).includes(globalUniqueIdentifier)) return false
      if (!this.instances[globalUniqueIdentifier].availablePermissions) return false
      let permissions = this.instances[globalUniqueIdentifier].availablePermissions
      if (!skipSimulation && useSettingsStore().simulatePermissions)
        permissions = permissions.filter(permission => useSettingsStore().simulatedPermissions.includes(permission))
      return permissions.includes(permission)
    },

    hasWildcardPermission(permissionWildcardString, overrideGlobalUniqueIdentifier = undefined, skipSimulation = false) {
      const globalUniqueIdentifier = overrideGlobalUniqueIdentifier ?? this.selectedInstanceId

      if (!globalUniqueIdentifier || !this.instances[globalUniqueIdentifier]?.availablePermissions) {
        return false
      }

      const permissions = this.instances[globalUniqueIdentifier].availablePermissions
      const permissionsToUse =
        !skipSimulation && useSettingsStore().simulatePermissions
          ? permissions.filter(p => useSettingsStore().simulatedPermissions.includes(p))
          : permissions

      const pattern = new RegExp('^' + permissionWildcardString.split('*').map(_.escapeRegExp).join('.*') + '$')

      return permissionsToUse.some(p => pattern.test(p))
    },

    hasAnyPermission(permissions, overrideGlobalUniqueIdentifier = undefined, skipSimulation = false) {
      for (const permission of permissions) {
        if (this.hasPermission(permission, overrideGlobalUniqueIdentifier, skipSimulation)) return true
      }
      return false
    },

    ensureSenatorExists() {
      if (this.instances['66']) return
      this.addNewBackendInstance(
        '66',
        'VetVise SSO',
        {
          remote: {
            hostname: 'senator.vms.dev.vetvise.com',
            port: 443,
            protocol: 'https',
          },
        },
        'senator'
      )
    },

    async getAccounts(globalUniqueIdentififer = this.selectedInstanceId, skipPermissionSimulation = false) {
      const accountResponse = await backendHandler.post(globalUniqueIdentififer, { path: '/api/accounts/query', skipPermissionSimulation })
      if (accountResponse.ok) return accountResponse.data
      return []
    },

    async listAccounts(globalUniqueIdentifier = this.selectedInstanceId) {
      const accountResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/accounts',
      })
      if (accountResponse.ok) return accountResponse.data
      return []
    },

    async queryAccounts(payload, globalUniqueIdentifier = this.selectedInstanceId) {
      const accountResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/accounts/query',
        data: payload,
      })
      if (accountResponse.ok) return accountResponse.data
      return []
    },

    async queryOneAccount(payload, globalUniqueIdentifier = this.selectedInstanceId) {
      const accountResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/accounts/queryOne',
        data: payload,
      })
      if (accountResponse.ok) return accountResponse.data
      return null
    },

    async queryEmailPasswordAuthRecords(payload, globalUniqueIdentifier = this.selectedInstanceId, skipPermissionSimulation = false) {
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/emailPassword/query',
        data: payload,
        skipPermissionSimulation,
      })
      if (response.ok) return response.data
      return []
    },

    async queryUsernamePasswordAuthRecords(payload, globalUniqueIdentifier = this.selectedInstanceId, skipPermissionSimulation = false) {
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/usernamePassword/authenticated/query',
        skipPermissionSimulation,
      })
      if (response.ok) return response.data
      return []
    },
    async queryRemoteAuthRecords(payload, globalUniqueIdentifier = this.selectedInstanceId, skipPermissionSimulation = false) {
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/auth/remote/query',
        data: payload,
        skipPermissionSimulation,
      })
      if (response.ok) return response.data
      return []
    },
    async removeTagFromAccount(accountId, tag, globalUniqueIdentifier = this.selectedInstanceId) {
      const accountResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/accounts/' + accountId,
      })
      if (!accountResponse.ok) return false
      const tags = accountResponse.data.tags
      if (!tags) return true
      const response = await backendHandler.patch(globalUniqueIdentifier, {
        path: '/api/accounts/' + accountId,
        data: { updates: { tags: tags.filter(existingTag => existingTag !== tag) } },
      })
      return response.ok
    },

    async addTagToAccount(accountId, tag, globalUniqueIdentifier = this.selectedInstanceId) {
      const accountResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/accounts/' + accountId,
      })
      if (!accountResponse.ok) return false
      const tags = accountResponse.data.tags || []
      if (tags.includes(tag)) return true
      tags.push(tag)
      const response = await backendHandler.patch(globalUniqueIdentifier, {
        path: '/api/accounts/' + accountId,
        data: { updates: { tags } },
      })
      return response.ok
    },

    async getCurrentEula(globalUniqueIdentifier) {
      return this.getEulaFile(globalUniqueIdentifier, 'current')
    },

    async getEulaFile(globalUniqueIdentifier, version = 'current') {
      const eulaResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/eulas/' + version,
        requiresAuth: false,
        responseType: 'blob',
      })
      if (!useEphemeralBackendInstancesStore().eulas[globalUniqueIdentifier]) useEphemeralBackendInstancesStore().eulas[globalUniqueIdentifier] = {}

      if (!eulaResponse.ok) {
        return false
      }
      const eulaURL = URL.createObjectURL(eulaResponse.data)
      const loadedVersion = eulaResponse.response.headers['content-disposition'].match(/filename="([^"]+)"/)[1]

      useEphemeralBackendInstancesStore().eulas[globalUniqueIdentifier][loadedVersion] = {
        url: eulaURL,
        version: loadedVersion,
      }
      if (version === 'current')
        useEphemeralBackendInstancesStore().eulas[globalUniqueIdentifier]['current'] = {
          url: eulaURL,
          version: loadedVersion,
        }
      return eulaURL
    },

    async loadOwnEulaHistory(globalUniqueIdentifier = undefined) {
      if (globalUniqueIdentifier === undefined) globalUniqueIdentifier = this.selectedInstanceId
      const historyResponse = await backendHandler.get(globalUniqueIdentifier, { path: '/api/eulaAcceptance' })
      if (!historyResponse.ok) return []
      return historyResponse.response.data
    },

    async loadEulaHistoryForAccount(accountId) {
      const historyResponse = await backendHandler.get(this.selectedInstanceId, { path: '/api/eulaAcceptance/' + accountId })
      if (!historyResponse.ok) return []
      return historyResponse.data
    },

    async revokeEula(globalUniqueIdentifier) {
      const revokeResponse = await backendHandler.delete(globalUniqueIdentifier, { path: '/api/eulaAcceptance' })
      if (!revokeResponse.ok) return false
    },

    async loadBackendLicenses() {
      const licenseResponse = await backendHandler.get(this.selectedInstanceId, { path: '/api/licenses' })
      if (!licenseResponse.ok) return []
      return licenseResponse.data
    },

    /**
     * Loads the image Url for the given Instance.
     * This Loads the Image Url from the Backend Identity Record.
     * If the Image Url is already loaded and not older than 7 days, it will not be reloaded. (except if force is set to true)
     * If the Instance has a ServiceProviderImageUrl set, the Image Url will not be loaded. (except if force is set to true)
     * @param {String} globalUniqueIdentifier
     * @param force
     * @param signal AbortSignal
     * @returns {Promise<boolean>}
     */
    async loadImage(globalUniqueIdentifier, force = false, signal = undefined) {
      if (
        !force &&
        this.instances[globalUniqueIdentifier].imageUrl &&
        this.instances[globalUniqueIdentifier].imageUrlLoadedTimestamp > Date.now() - 1000 * 60 * 60 * 24 * 7
      )
        return
      if (!force && this.instances[globalUniqueIdentifier].useServiceProviderImageUrl) return true
      const urlResponse = await backendHandler.get(globalUniqueIdentifier, { path: '/api/identity/imageUrl', signal })
      if (!urlResponse.ok) return false
      this.updateInstance(globalUniqueIdentifier, 'imageUrl', urlResponse.data)
      this.updateInstance(globalUniqueIdentifier, 'imageUrlLoadedTimestamp', Date.now())
    },

    async getDisplayName(globalUniqueIdentifier) {
      if (globalUniqueIdentifier === undefined) globalUniqueIdentifier = this.selectedInstanceId
      const accountResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/accounts/queryOne',
        data: { query: this.getAccountIdForInstance(globalUniqueIdentifier) },
      })
      return accountResponse.data.displayName
    },

    async queryPens(globalUniqueIdentifier = this.selectedInstanceId) {
      const response = await backendHandler.post(globalUniqueIdentifier, { path: '/api/pens/query' })
      if (!response.ok) return null
      return response.data
    },

    async genericQuery(globalUniqueIdentifier = this.selectedInstanceId, path, payload) {
      const response = await backendHandler.post(globalUniqueIdentifier, { path, data: payload })
      if (!response.ok) return null
      return response.data
    },

    async queryPenObservations(globalUniqueIdentifier = this.selectedInstanceId) {
      const response = await backendHandler.post(globalUniqueIdentifier, { path: '/api/pen-observations/query' })
      if (!response.ok) return null
      return response.data
    },

    async createPenObservation(observationData, globalUniqueIdentifier = this.selectedInstanceId) {
      const response = await backendHandler.post(globalUniqueIdentifier, { path: '/api/pen-observations', data: observationData })
      if (!response.ok) return null
      return response.data
    },

    async deletePenObservation(penObservationId, globalUniqueIdentifier = this.selectedInstanceId) {
      const response = await backendHandler.delete(globalUniqueIdentifier, { path: '/api/pen-observations/' + penObservationId })
      if (!response.ok) return null
      return response.data
    },

    async updatePenObservation(penObservationId, observationData, globalUniqueIdentifier = this.selectedInstanceId) {
      const response = await backendHandler.patch(globalUniqueIdentifier, {
        path: '/api/pen-observations/' + penObservationId,
        data: observationData,
      })
      if (!response.ok) return null
      return response.data
    },

    updateInstanceFromServiceProvider(serviceProvider) {
      if (!this.instances[serviceProvider.globalUniqueIdentifier]) return
      let nameToUse = serviceProvider.customizedServiceProviderName || serviceProvider.serviceProviderName
      this.updateInstance(serviceProvider.globalUniqueIdentifier, 'name', nameToUse)
      this.updateInstance(serviceProvider.globalUniqueIdentifier, 'color', serviceProvider.foregroundColor)
      this.updateInstance(serviceProvider.globalUniqueIdentifier, 'bgcolor', serviceProvider.backgroundColor)
      this.updateInstance(serviceProvider.globalUniqueIdentifier, 'order', serviceProvider.instanceListOrder || 500)
      this.updateInstance(serviceProvider.globalUniqueIdentifier, 'folderName', serviceProvider.folderName)

      if (serviceProvider.imageUrl) {
        this.updateInstance(serviceProvider.globalUniqueIdentifier, 'serviceProviderImageUrl', serviceProvider.imageUrl)
        this.updateInstance(serviceProvider.globalUniqueIdentifier, 'useServiceProviderImageUrl', true)
      } else {
        this.updateInstance(serviceProvider.globalUniqueIdentifier, 'serviceProviderImageUrl', '')
        this.updateInstance(serviceProvider.globalUniqueIdentifier, 'useServiceProviderImageUrl', false)
      }
    },

    async loadDiscoveredNetworkDevices(globalUniqueIdentifier = undefined, signal) {
      const response = await backendHandler.get(globalUniqueIdentifier, { path: '/api/network/discover', signal })
      if (!response.ok) return []
      return response.data
    },

    async updateOUI(globalUniqueIdentifier = undefined, signal) {
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/network/discover/update-oui',
        signal,
      })
      if (!response.ok) return false
      return true
    },

    async addServiceProvidersToInstanceList() {
      const settingsStore = useSettingsStore()
      const serviceProviderStore = useServiceProviderStore()
      const serviceProviders = await serviceProviderStore.loadServiceProviders()
      if (!serviceProviders) return
      for (const serviceProvider of serviceProviders) {
        this.updateInstanceFromServiceProvider(serviceProvider)
      }
      if (!settingsStore.autoAddServiceProviders) return

      const promises = []

      for (const serviceProvider of serviceProviders) {
        if (settingsStore.serviceProvidersExcludedFromAutoAdd.includes(serviceProvider.globalUniqueIdentifier)) continue
        if (this.instances[serviceProvider.globalUniqueIdentifier]) continue
        const promise = backendHandler
          .get('66', { path: 'api/serviceProviders/' + serviceProvider.serviceProviderId + '/discoveryObject' })
          .then(discoveryObjectResponse => {
            if (!discoveryObjectResponse.ok) return
            const discoveryObject = discoveryObjectResponse.data
            this.addNewBackendInstance(
              serviceProvider.globalUniqueIdentifier,
              serviceProvider.customizedServiceProviderName || serviceProvider.serviceProviderName,
              discoveryObject,
              serviceProvider.instanceType ?? 'barn'
            )
            this.updateInstanceFromServiceProvider(serviceProvider)
          })
          .catch(e => {
            useLogsStore().addLogEntry({
              message: 'Error while adding service provider ' + serviceProvider.serviceProviderName + ' to instance list',
              error: e,
              tag: 'backendInstances',
              globalUniqueIdentifier: serviceProvider.globalUniqueIdentifier,
              level: 'ERROR',
              httpMethod: 'GET',
            })
          })
        promises.push(promise)
      }
      await Promise.all(promises)
    },

    async addInstanceFromDiscoveryString(discoveryString, guidOverride = undefined) {
      const discoveryObject = buildDiscoveryObject(discoveryString)
      if (!discoveryObject) return false
      if (!guidOverride && !discoveryObject.gateway?.id) return false
      const globalUniqueIdentifier = guidOverride ? guidOverride : discoveryObject.gateway.id
      if (!this.instances[globalUniqueIdentifier]) {
        this.addNewBackendInstance(globalUniqueIdentifier, 'VetVise SSO', discoveryObject, 'senator')
      }
      await backendHandler.getOrCreateBackendAPIInstance(globalUniqueIdentifier).triggerScheduledQuickConnectionCheck()
      return globalUniqueIdentifier
    },

    async setPasswordForProvisionedAccount({ id, token, password, passwordRepetition, globalUniqueIdentifier, discoveryString, acceptEula }) {
      await this.addInstanceFromDiscoveryString(discoveryString, globalUniqueIdentifier)
      const response = await backendHandler.post(globalUniqueIdentifier, {
        requiresAuth: false,
        path: '/api/auth/emailPassword/setPassword',
        data: {
          authenticationId: id,
          password: password,
          passwordRepetition: passwordRepetition,
          verificationToken: token,
          acceptEula: acceptEula,
        },
      })
      return response.ok
    },

    async ensureOwnPermissionsLoaded(globalUniqueIdentifier) {
      await SyncableHandler.ensureSyncable(OwnPermissionsSyncable, globalUniqueIdentifier)
    },

    keepOwnPermissionsLoaded(globalUniqueIdentifier) {
      return SyncableHandler.addSyncable(OwnPermissionsSyncable, globalUniqueIdentifier)
    },
    keepVersionLoaded(globalUniqueIdentifier) {
      return SyncableHandler.addSyncable(VersionSyncable, globalUniqueIdentifier)
    },

    async restartVMS(globalUniqueIdentifier = this.selectedInstanceId) {
      await backendHandler.put(globalUniqueIdentifier, { path: '/api/stop/instantForceRestart' })
    },

    async rebootHostOS(globalUniqueIdentifier = this.selectedInstanceId) {
      const response = await backendHandler.put(globalUniqueIdentifier, { path: '/api/systemManagement/rebootHostOs' })
      return response.ok
    },

    async loadLogs(globalUniqueIdentifier = this.selectedInstanceId, from, to, logLevels = [], moduleId = null, logger) {
      const body = { from, to, limit: -1, predicate: { op: 'and', apply: [] } }
      if (logLevels && logLevels.length > 0) {
        body.predicate.apply.push({ op: 'in', path: '/level', value: logLevels })
      }
      if (moduleId) {
        body.predicate.apply.push({ op: 'test', path: '/module', value: moduleId, ignore_case: true })
      }
      const response = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/logs/' + logger,
        data: body,
      })
      if (!response.ok) return null
      return response.data
    },
  },
})
